import { APP_BASE_HREF } from "@angular/common";
import { AfterViewInit, AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Inject, OnInit, ViewChild } from "@angular/core";
import { Subject, takeUntil, forkJoin } from "rxjs";

import { IUserData, IBaseSettings, BaseComponent, IFunction } from "@indusoft/angular-base";
import { AuthenticationService } from "@indusoft/authenticator";
import { OverlayWindowPosition, OverlayWindowService, OverlayWindowSettings } from "@indusoft/overlay-window";

import { RoutesGlossary } from "../../common/glossary/routes-glossary";
import { AuthQueryParams } from "../../auth/app/glossary/auth-query-params-glossary";
import { ConfigurationHttpService } from "../../common/services/configuration.service";
import { AuthHttpService } from "../../auth/app/services/auth/auth-http.service";
import { LcInterceptor } from '../../common/services/lc-interceptor';
import { LicenseHttpService } from '../../common/services/license-http-service';
import { IndusoftUIInteractionService } from "../../common/services/indusoft-ui-interaction-service";
import { LocalStorageService } from '@indusoft/angular-base-services';
import { LicenseComponent } from '../../license/app/license.component';

@Component({
    selector: 'header',
    templateUrl: 'header.component.html',
    styleUrls: ['header.component.css']
})
export class HeaderComponent implements OnInit, AfterViewInit, AfterViewChecked {
    public settingsPath: string = "./settings/header_base_settings.json";
    public userData: IUserData = { id: 0, name: '', login: '' };
    public settings?: Partial<IBaseSettings>;
    public showHeader: boolean = true;
    public showSwitch: boolean = false;
    public isAdmin: boolean = false;
    public isAdminMode: boolean = window.location.pathname.toLowerCase() === this.baseHref + RoutesGlossary.WebAdmin.toLowerCase();
    public authProviderName: string | null = null;
    private aasEnabled: boolean = false;
    private idsLsEnabled: boolean = false;
    private unsubscribeSubject$ = new Subject<void>();

    constructor(
        @Inject(APP_BASE_HREF) public baseHref: string,
        private cdr: ChangeDetectorRef,
        private elRef: ElementRef,
        private authHttpService: AuthHttpService,
        private configService: ConfigurationHttpService,
        private authentificatorService: AuthenticationService,
        private indusoftUIInteractionService: IndusoftUIInteractionService,
        private licenseService: LicenseHttpService,
        private lcInterceptor: LcInterceptor,
        private localStorageService: LocalStorageService,
        private readonly overlayWindowService: OverlayWindowService) {
    }

    @ViewChild('headerBase')
    public headerBase: BaseComponent;

    ngOnInit(): void {
        (window as any).callShowLicenseInfo = () => {
            this.showLicenseInfo();
        };

        this.lcInterceptor.init();

        this.loadConfiguration();

        const headerSettingsLoaded = new Event('headersettingsloaded');
        document.dispatchEvent(headerSettingsLoaded);

        if ((window.location.pathname.toLowerCase() === this.baseHref + RoutesGlossary.Login.toLowerCase()) ||
            (window.location.pathname.toLowerCase() === this.baseHref + RoutesGlossary.LoginAas.toLowerCase())) {
            this.showHeader = false;
        }

        this.indusoftUIInteractionService.initialize();
        this.interactWithIndusoftUI();
        document.addEventListener(IndusoftUIInteractionService.CHECK_SHOW_SWITCH_EVENT_NAME, this.showSwitchEventHandler);
    }

    ngAfterViewInit(): void {
        const login = this.elRef?.nativeElement.getAttribute('[user]');

        this.userData.name = login;
        this.userData.login = login;
        this.isAdmin = (this.elRef?.nativeElement.getAttribute('[isAdmin]')?.toLowerCase() === "true") ?? false;

        this.cdr.detectChanges();

        document.addEventListener('checkShowSwitch', (event: CustomEvent) => {
            this.showSwitch = event.detail;
        });

        if (this.aasEnabled === true) {
            this.addAasLogoutSubscription();
        }
    }

    ngAfterViewChecked(): void {
        this.redrawDropdownMenu();
    }

    public ngOnDestroy(): void {
        document.removeEventListener(IndusoftUIInteractionService.CHECK_SHOW_SWITCH_EVENT_NAME, this.showSwitchEventHandler);
        this.unsubscribeSubject$.next();
        this.unsubscribeSubject$.complete();
    }

    private loadConfiguration(): void {
        forkJoin({
            idsLs: this.configService.getIdsLsConfiguration(),
            auth: this.configService.getAuthConfiguration(),
            license: this.licenseService.getLicenseInfo()
        }).subscribe({
            next: ({ idsLs, auth, license }) => {
                this.authProviderName = auth?.providerName || null;
                this.aasEnabled = this.authProviderName === "AAS";
                this.idsLsEnabled = idsLs.isEnabled;
                this.localStorageService.setValue('idsLsEnabled', this.idsLsEnabled.toString());
                this.localStorageService.setValue('licenseInfo', JSON.stringify(license?.licenseInfo));
            },
            error: (error) => {
                console.error('Failed to load configuration', error);
                this.authProviderName = null;
                this.aasEnabled = false;
                this.idsLsEnabled = false;
                this.aasEnabled = false;
            }
        });
    }

    public redrawDropdownMenu() {
        if (!this.headerBase?.dropdownMenu) return;

        const logoutItemSelector = this.headerBase?.dropdownMenu?.find(
            x => x.func == 'logOut'
        );

        if (logoutItemSelector) {
            logoutItemSelector.functionEvent = async () => await this.onLogoutClick();
            this.cdr.detectChanges();
        }

        if (!this.isAdmin)
            return;

        const webAdminMenuItemSelector = this.headerBase.dropdownMenu.find(
            x => x.func == 'webAdmin');

        if (!webAdminMenuItemSelector) {
            const webAdminMenuItem: Partial<IFunction> = {
                func: "webAdmin",
                name: this.isAdminMode ? "Выйти из режима администрирования" : "Администрирование",
                active: true,
                icon: this.isAdminMode ? "fa-sign-out-alt" : "fa-cogs",
                url: this.baseHref + (this.isAdminMode ? "" : RoutesGlossary.WebAdmin)
            };
            this.headerBase.dropdownMenu.unshift(webAdminMenuItem);
        }
    }

    public showLicenseInfo(): void {
        const settings = new OverlayWindowSettings(undefined, '650px', undefined, false, false, OverlayWindowPosition.LeftBottom);
        this.overlayWindowService.showWindow(LicenseComponent, settings);
    }

    public async onLogoutClick() {
        this.authHttpService
            .logOut()
            .pipe(takeUntil(this.unsubscribeSubject$))
            .subscribe({
                next: async () => {
                    await this.logoutFromAasIfEnabled();
                    this.redirectToLoginPage();
                },
                error: async () => {
                    await this.logoutFromAasIfEnabled();
                    this.redirectToLoginPage();
                }
            });
    }

    private async logoutFromAasIfEnabled(): Promise<void> {
        if (!this.aasEnabled) {
            return;
        }

        try {
            await this.authentificatorService.logout();
        } catch {
            // pass
        }
    }

    private addAasLogoutSubscription() {
        this.authentificatorService.isLoggedIn$
            .pipe(takeUntil(this.unsubscribeSubject$))
            .subscribe(async aasLoggedIn => {
                if (aasLoggedIn === false) {
                    this.authHttpService
                        .logOut()
                        .subscribe({
                            next: () => this.redirectToLoginPage(),
                            error: r => this.redirectToLoginPage(),
                        });
                }
            });
    }

    private redirectToLoginPage() {
        if (this.aasEnabled === true) {
            window.location.href = RoutesGlossary.Login;
        }
        else {
            window.location.href = RoutesGlossary.Login + `?${AuthQueryParams.IGNORE_DOMAIN_AUTOLOGIN}=true`;
        }
    }

    public toggleVisible = (): void => {
        this.indusoftUIInteractionService.isVisible = !this.indusoftUIInteractionService.isVisible;
    }

    // Обработчик события из IndusoftUI
    private showSwitchEventHandler = (event: Event) => {
        this.interactWithIndusoftUI();
    }

    public get tooltipUIPanelButton(): string {
        return this.indusoftUIInteractionService.isVisible === true ? "Скрыть панель" : "Показать панель";
    }

    private interactWithIndusoftUI(): void {
        this.showSwitch = this.indusoftUIInteractionService.isShowSwitch;
    }

    public onManualInputMenuItemClicked() {
        window['globalFunctions'].prototype.openManualInputWindow();
    }

    public onControlMeasurementMenuItemClicked() {
        window['globalFunctions'].prototype.openControlMeasurementWindow();
    }

    public onAuditMenuItemClicked() {
        window['globalFunctions'].prototype.openAuditWindow();
    }

    public onSwitchMainWindowViewClicked() {
        window['globalFunctions'].prototype.switchMainWindowView();
    }
}
